/**
 * SKU mapping maintenance — LSK's admin owns these, not the integration team.
 *
 * A tag carries a colour or a set-piece code; SQL Accounting only knows the parent. These mappings
 * are what turn one into the other, so they decide which item code lands on a goods received note.
 * They were loaded once from a spreadsheet, which meant every new product line came back to us.
 *
 * Three things shape this screen:
 *
 *   SEARCH, NEVER LIST — 198,180 colour mappings. A browsable list is not a thing that can exist.
 *   BULK FIRST — a product line is ~81 colours; nobody types that. The dry run reports what would
 *     happen before anything is written, because a bad file here becomes a wrong posting later.
 *   DEACTIVATE, NEVER DELETE — deleting a mapping makes every EPC already tagged with that code
 *     unresolvable, and the picker just sees a tag that stopped working.
 */
import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { api } from '../lib/api';
import { parseDelimited, toMappingRows, CSV_COLUMNS } from '../../../src/domain/skumap/csv';
import { generateColourFamily, generateSetFamily } from '../../../src/domain/skumap/generate';
import { decideFamilyKind, baseSetPieces } from '../../../src/domain/skumap/familyKind';
import { parseSeparateLines, setPieceCodes } from '../../../src/domain/skumap/setPieceCodes';
import { parsePieces } from '../../../src/domain/skumap/setPieces';
import { inChunks } from '../../../src/domain/skumap/chunk';

/**
 * Rows per request.
 *
 * A full product line is ~10,000 rows, past Fastify's 1 MB body limit and the 5,000-row cap. 1,000
 * mapping rows is roughly 120 KB — comfortably inside both, and small enough that a failure costs
 * one batch rather than the lot. Product creation is capped lower because the server refuses more
 * than SKUMAP_PRODUCT_MAX (500) in one go.
 */
const UPLOAD_BATCH = 1000;
const CREATE_BATCH = 400;
/** The mass check. Matches the server's own per-request cap, so a batch is never truncated. */
const CHECK_BATCH = 500;
import { Card, Table, Loading, Empty, cell } from '../components/ui';

type ColourRow = {
  child_sku: string;
  parent_sku: string;
  colour_code: string | null;
  colour_name: string | null;
  is_active: number;
  updated_by: string | null;
  source: string | null;
};
type SplitRow = {
  child_sku: string;
  parent_sku: string;
  piece: string;
  qty_per_set: string;
  colour_code: string | null;
  colour_name: string | null;
  is_active: number;
  updated_by: string | null;
  source: string | null;
};
type Family = {
  parentSku: string;
  colour: { total: number; active: number };
  /** `coloured` are colour × piece; `base` is the plain fabric pair. See the chip below. */
  split: { total: number; active: number; coloured: number; base: number };
};
type SearchResult = {
  ok: boolean;
  code: string;
  colour: ColourRow[];
  split: SplitRow[];
  families: Family[];
};

/** One line per parent for the mass check — counts plus a verdict. */
type FamilyLine = {
  itemCode: string;
  status:
    | 'not-an-item'
    | 'needs-split'
    | 'needs-colours'
    | 'colours-not-split'
    | 'no-fallback'
    | 'duplicate-colours'
    | 'ready';
  needs: string;
  inAccounts: boolean;
  looksLikeSet: boolean;
  colours: number;
  splitColoured: number;
  splitBase: number;
};

const STATUS_STYLE: Record<FamilyLine['status'], { label: string; cls: string }> = {
  'not-an-item': { label: 'wrong code', cls: 'bg-alert/10 text-alert' },
  'needs-split': { label: 'separate first', cls: 'bg-amber-100 text-amber-900' },
  'needs-colours': { label: 'no colours yet', cls: 'bg-amber-50 text-amber-800' },
  // The colours exist but in the pre-separation form — they must be added again as pieces.
  'colours-not-split': { label: 'colours need redoing', cls: 'bg-amber-100 text-amber-900' },
  'no-fallback': { label: 'no plain pair', cls: 'bg-amber-50 text-amber-800' },
  'duplicate-colours': { label: 'old colours unused', cls: 'bg-slate-100 text-slate-600' },
  ready: { label: 'ready', cls: 'bg-emerald-50 text-emerald-700' },
};

type Rejection = {
  row: number;
  childSku: string;
  parentSku: string;
  reason: string;
  /** Carried so a rejected row can be CREATED with its real colour name, not its colour code. */
  piece?: string;
  colourCode?: string;
  colourName?: string;
};
type UploadResult = {
  dryRun: boolean;
  wouldWrite?: number;
  written?: number;
  rejected: Rejection[];
  /** Live pieces this save retires: the item was split differently before. See retirePieces.ts. */
  retire?: Array<{ childSku: string; parentSku: string; piece: string }>;
  retired?: number;
};

/** Creating the child product in Smartlink — the step that used to come back to us. */
type CreateResult = {
  ok: boolean;
  dryRun: boolean;
  simulated?: boolean;
  wouldCreate?: number;
  created?: number;
  error?: string;
  create?: Array<{
    sku: string;
    styleName: string | null;
    sizeName: string | null;
    parentSku: string;
    parentName: string | null;
  }>;
  skipped: Array<{ childSku: string; reason: string }>;
  refused: Array<{ childSku: string; reason: string }>;
};

/**
 * A rejection this screen can now fix by itself.
 *
 * Only "not in Smartlink". A parent missing from SQL Accounting is NOT fixable here — that would
 * mean inventing an item the accounts do not have.
 */
const MISSING_IN_SMARTLINK = /is not in Smartlink/;

/**
 * Both a pasted selection and an uploaded file go through the same parser, so a comma inside a
 * colour name — "CTE 002, PEWTER" — cannot shift the columns one way and not the other.
 */
function parseRows(text: string, kind: 'colour' | 'split') {
  return toMappingRows(parseDelimited(text), kind).filter(
    (r) => r.childSku !== '' || r.parentSku !== '',
  );
}

/** A template with the right columns and one worked example, so nobody guesses the order. */
function downloadTemplate(kind: 'colour' | 'split') {
  const example =
    kind === 'colour'
      ? ['HBTF182CTE002', 'HBTF182FB1', 'CTE002', 'CTE 002 PEWTER']
      : // The 5th column names the product when a new piece is created in Smartlink. Optional:
        // a four-column file still parses.
        ['BFJOY152(12)ELGBA', 'BFJOY152(12)FB1AB', 'A', '1', 'ELGB', 'ELGB MISTY'];
  const csv = `${CSV_COLUMNS[kind].join(',')}\n${example.join(',')}\n`;
  // A BOM so Excel opens it as UTF-8 rather than mangling any accented colour name.
  const blob = new Blob([`\ufeff${csv}`], { type: 'text/csv;charset=utf-8' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = `sku-mapping-${kind}-template.csv`;
  a.click();
  URL.revokeObjectURL(url);
}

export default function SkuMapping() {
  const qc = useQueryClient();
  const [code, setCode] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [includeInactive, setIncludeInactive] = useState(false);
  const [kind, setKind] = useState<'colour' | 'split'>('colour');
  const [paste, setPaste] = useState('');
  const [result, setResult] = useState<UploadResult | null>(null);
  const [created, setCreated] = useState<CreateResult | null>(null);
  const [showAllRejected, setShowAllRejected] = useState(false);
  const [progress, setProgress] = useState<{ done: number; total: number } | null>(null);
  /**
   * Which format the rows in the box were BUILT for, when a generator built them.
   *
   * The two layouts share their first two columns, so a set-piece row read as a colour row still
   * has a valid child and parent — validation passes, and the piece silently lands in the colour
   * table where quantities ADD instead of pairing. Nothing downstream would catch it. Remembering
   * the source is what makes the mismatch visible.
   */
  const [pasteSource, setPasteSource] = useState<'colour' | 'split' | null>(null);
  const [bulkCodes, setBulkCodes] = useState('');
  const [sepCodes, setSepCodes] = useState('');
  const [sepPieces, setSepPieces] = useState('AB');
  /** Set while Save runs — see createProducts.onSuccess. */
  const savingAll = useRef(false);
  const [fileName, setFileName] = useState('');
  // The generator: parents in one box, colour codes and names in two more.
  const [genParents, setGenParents] = useState('');
  const [genCodes, setGenCodes] = useState('');
  const [genNames, setGenNames] = useState('');

  const search = useQuery({
    queryKey: ['skumap', submitted, includeInactive],
    enabled: submitted !== '',
    queryFn: () =>
      api.get<SearchResult>(
        `/api/v1/skumap/search?code=${encodeURIComponent(submitted)}&includeInactive=${includeInactive}`,
      ),
  });

  const upload = useMutation({
    mutationFn: async (apply: boolean): Promise<UploadResult> => {
      const rows = parseRows(paste, kind);
      // Every parent's pieces across the WHOLE paste, sent with each batch: one colour family can
      // straddle two batches, and a batch must not read "no SD here" as "this bed has no SD".
      const pieceSets: Record<string, string[]> = {};
      if (kind === 'split') {
        for (const r of rows as Array<Record<string, string | undefined>>) {
          const parent = (r['parentSku'] ?? '').trim().toUpperCase();
          const piece = (r['piece'] ?? '').trim().toUpperCase();
          if (parent === '' || piece === '') continue;
          const list = (pieceSets[parent] ??= []);
          if (!list.includes(piece)) list.push(piece);
        }
      }
      setProgress({ done: 0, total: rows.length });
      const parts = await inChunks(
        rows,
        UPLOAD_BATCH,
        (batch, offset) =>
          api
            .post<UploadResult>('/api/v1/skumap/upload', {
              kind,
              apply,
              rows: batch,
              ...(kind === 'split' ? { pieceSets } : {}),
            })
            .then((r) => ({
              ...r,
              // Rejections are numbered within their batch. Renumber to the line the user pasted,
              // or batch 6's "row 3" reads as row 3 when it is row 5,003.
              rejected: r.rejected.map((x) => ({ ...x, row: x.row + offset })),
            })),
        (done, total) => setProgress({ done, total }),
      );
      setProgress(null);
      return {
        dryRun: parts[0]?.dryRun ?? !apply,
        wouldWrite: parts.reduce((n, p) => n + (p.wouldWrite ?? 0), 0),
        written: parts.reduce((n, p) => n + (p.written ?? 0), 0),
        rejected: parts.flatMap((p) => p.rejected),
        // Each batch reports the retirements for ITS parents; a parent in two batches is in both.
        retire: [
          ...new Map(parts.flatMap((p) => p.retire ?? []).map((x) => [x.childSku, x])).values(),
        ],
        retired: parts.reduce((n, p) => n + (p.retired ?? 0), 0),
      };
    },
    onSuccess: (r) => {
      setResult(r);
      if (!r.dryRun) void qc.invalidateQueries({ queryKey: ['skumap'] });
    },
    onError: () => setProgress(null),
  });

  /**
   * Save does the whole job: create whatever is missing, then write the mappings.
   *
   * It used to be Check → Preview → Create → Save, and stopping after Create looked finished —
   * the rejections cleared, so 7,992 products existed with no mappings behind them. Folding the
   * preview into Check and the creation into Save leaves two buttons and no half-done state.
   */
  const saveAll = useMutation({
    mutationFn: async () => {
      savingAll.current = true;
      try {
        if (creatable.length > 0) {
          const c = await createProducts.mutateAsync(true);
          if (!c.ok) throw new Error(c.error ?? 'creating the products failed');
        }
        return await upload.mutateAsync(true);
      } finally {
        savingAll.current = false;
      }
    },
  });

  /**
   * Create the missing child products in Smartlink.
   *
   * Two steps, like the upload, and for a sharper reason: there is no delete endpoint, so a product
   * created by mistake can be deactivated but never removed.
   */
  const createProducts = useMutation({
    mutationFn: async (apply: boolean): Promise<CreateResult> => {
      /**
       * Build the rows to create from the PASTED ROWS, not from the rejections.
       *
       * This used to look each rejection up in a map keyed by child code, and take the colour and
       * piece from whatever came back. When that lookup missed — for any reason, and it did — the
       * row still went out, just with a blank colour and a blank piece, and the product got named
       * after its parent. 10,360 products were created that way.
       *
       * The pasted row already HAS its colour and piece. So filter the rows themselves and let each
       * carry its own values; the rejections only say WHICH ones are missing from Smartlink. There
       * is no lookup left to miss.
       */
      const needed = new Set(
        (result?.rejected ?? [])
          .filter((r) => MISSING_IN_SMARTLINK.test(r.reason))
          .map((r) => r.childSku.trim().toUpperCase()),
      );
      const rows = parseRows(paste, kind)
        .filter((r) => needed.has((r.childSku ?? '').trim().toUpperCase()))
        .map((r) => ({
          childSku: String(r.childSku ?? ''),
          parentSku: String(r.parentSku ?? ''),
          colourName: String((r as { colourName?: string }).colourName ?? ''),
          colourCode: String((r as { colourCode?: string }).colourCode ?? ''),
          piece: String((r as { piece?: string }).piece ?? ''),
        }));

      // Every code that needs creating must appear among the pasted rows. Compare the SETS, not
      // the counts — a paste can legitimately repeat a child code, and a count check would then
      // fail on a file that is perfectly fine.
      const found = new Set(rows.map((r) => r.childSku.trim().toUpperCase()));
      const unmatched = [...needed].filter((c) => !found.has(c));
      if (unmatched.length > 0) {
        throw new Error(
          `${unmatched.length} of ${needed.size} code(s) to create are not in the pasted rows ` +
            `(e.g. ${unmatched.slice(0, 3).join(', ')}). Re-run Check and try again.`,
        );
      }

      /**
       * A PREVIEW only needs to show what the names will look like — one batch is enough for that,
       * and 19,680 rows would otherwise mean fifty round trips before the Save button even turns
       * on. Applying still goes through every row.
       */
      const toSend = apply ? rows : rows.slice(0, CREATE_BATCH);

      setProgress({ done: 0, total: toSend.length });
      // Smaller batches than the upload: the server refuses more than SKUMAP_PRODUCT_MAX in one
      // request, and each row here creates a product that cannot be deleted afterwards.
      const parts = await inChunks(
        toSend,
        CREATE_BATCH,
        (batch) =>
          api.post<CreateResult>('/api/v1/skumap/create-products', {
            apply,
            kind,
            // Forward the colour and piece, not just the two codes. Sending only the codes made
            // every set piece take its colour CODE as its name — `(SRAQ)` beside a parent
            // reading `(SERENE AQUA)`.
            rows: batch.map((r) => ({
              childSku: r.childSku,
              parentSku: r.parentSku,
              ...(r.piece ? { piece: r.piece } : {}),
              ...(r.colourCode ? { colourCode: r.colourCode } : {}),
              ...(r.colourName ? { colourName: r.colourName } : {}),
            })),
          }),
        (done, total) => setProgress({ done, total }),
      );
      setProgress(null);

      return {
        ok: parts.every((p) => p.ok),
        dryRun: parts[0]?.dryRun ?? !apply,
        simulated: parts.some((p) => p.simulated),
        wouldCreate: parts.reduce((n, p) => n + (p.wouldCreate ?? 0), 0),
        created: parts.reduce((n, p) => n + (p.created ?? 0), 0),
        // First error wins: they are almost always the same cause repeated.
        ...(parts.find((p) => p.error) ? { error: parts.find((p) => p.error)?.error } : {}),
        create: parts.flatMap((p) => p.create ?? []).slice(0, 100),
        skipped: parts.flatMap((p) => p.skipped),
        refused: parts.flatMap((p) => p.refused),
      };
    },
    onSuccess: (r) => {
      setCreated(r);
      // Re-check straight away: the rows that were rejected should now pass, and seeing that
      // happen is the confirmation that the products really landed.
      //
      // NOT when Save is the caller. Save writes next, and its own validation is the re-check. Run
      // alongside it, this preview raced the write, ran before Smartlink showed the new products,
      // answered LAST and replaced the save's result — the first UPF/LPF/SD split (2026-10-03)
      // saved correctly and the screen still listed every row as "not in Smartlink".
      if (!r.dryRun && r.ok && !savingAll.current) upload.mutate(false);
    },
    onError: () => setProgress(null),
  });

  /**
   * "This item is not a set, whatever its description says."
   *
   * `looksLikeSet` reads `(A+B)` out of the item-master description. That is a reading of prose,
   * and LSK know their packing: BFJOY152D124DW10FB1 says `(A+B)` and ships as one box. Without a
   * way to say so, deactivating its pieces only moved it from "is a set" to "needs separating" —
   * equally blocked, with no way out of the screen.
   */
  const markNotASet = useMutation({
    mutationFn: (v: { itemCode: string; notASet: boolean; reason?: string }) =>
      api.post('/api/v1/skumap/family-override', v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['skumap'] }),
  });

  const setActive = useMutation({
    // `kind` travels with the ROW, not with the page's upload mode. They are different things: a
    // search shows a code's colour mappings AND its set pieces together, and either can be
    // deactivated while the uploader above is set to the other.
    mutationFn: (v: { childSku: string; active: boolean; kind: 'colour' | 'split' }) =>
      api.post('/api/v1/skumap/active', v),
    onSuccess: () => void qc.invalidateQueries({ queryKey: ['skumap'] }),
  });

  /** One code per line, so a spreadsheet column pastes straight in. */
  const bulkList = bulkCodes
    .split('\n')
    .map((l) => l.trim())
    .filter(Boolean);
  /**
   * Checked in batches, so there is no ceiling on how many can be pasted.
   *
   * The server caps each request at 500 and used to slice quietly — paste 600 and you got 500
   * answers with nothing to say the other hundred were never looked at. Batching removes the cap
   * entirely; the `dropped` count below is a backstop in case one ever bites again.
   */
  const bulkCheck = useMutation({
    mutationFn: async (codes: string[]) => {
      setProgress({ done: 0, total: codes.length });
      const parts = await inChunks(
        codes,
        CHECK_BATCH,
        (batch) =>
          api.post<{ items: FamilyLine[]; asked: number; checked: number; dropped: number }>(
            '/api/v1/skumap/item-kind',
            { codes: batch },
          ),
        (done, total) => setProgress({ done, total }),
      );
      setProgress(null);
      return {
        items: parts.flatMap((p) => p.items),
        dropped: parts.reduce((n, p) => n + (p.dropped ?? 0), 0),
      };
    },
    onError: () => setProgress(null),
  });

  /**
   * Separating sets: the parent code is the ONLY thing typed.
   *
   * Both child codes follow from it — `…FB1AB` pairs with `…FB1A` and `…FB1B`. Verified against
   * all 1,848 live base pieces, where this derives 1,846. Six split parents do not end in `AB` and
   * follow no rule; those are reported rather than guessed, and go in by paste.
   */
  // Per-line pieces win over the default: one batch can mix a two-box set with a bed split into
  // UPF/LPF/SD. Pasted from Excel in either column order — see parseSeparateLines.
  const sepList = parseSeparateLines(sepCodes, sepPieces);
  const sepRows = sepList.flatMap((r) => setPieceCodes(r.code, r.pieces));
  // Say WHY per line: "UFP is not a piece" is fixable; "no piece codes" is not.
  const sepUndecodable = sepList
    .filter((r) => setPieceCodes(r.code, r.pieces).length === 0)
    .map((r) => {
      const p = parsePieces(r.pieces);
      return `${r.code} (${p.ok ? 'no piece codes could be worked out' : p.error})`;
    });

  /**
   * Does the accounting description agree these are sets?
   *
   * The derivation will happily append a letter to ANY code, so a headboard pasted here would
   * produce two piece codes that no tag will ever carry. The description is the check — the same
   * `A+B` marker used everywhere else.
   */
  const sepKind = useQuery({
    queryKey: ['skumap', 'sep-kind', sepList.map((r) => r.code).join(',')],
    enabled: sepList.length > 0,
    queryFn: () =>
      api.post<{
        items: Array<{
          itemCode: string;
          looksLikeSet: boolean;
          isSplit: boolean;
          inAccounts: boolean;
        }>;
      }>('/api/v1/skumap/item-kind', { codes: sepList.map((r) => r.code) }),
  });
  /**
   * Two different answers that were being reported as one.
   *
   *   notSets  — SQL Accounting HAS the item and does not describe it as multi-piece. A real
   *              warning: splitting it makes codes no tag will carry.
   *   unknown  — SQL Accounting does not have the item at all, as far as we know. That is usually
   *              a typo, but it is also what a code created THIS MORNING looks like: the item
   *              master mirror refreshes once a night. `BFJOY152D124DW10FB1AB` was created on
   *              2026-09-17 and reported as "not a multi-piece item" — a statement about the item,
   *              when the truth was we had not got it yet.
   */
  const sepNotSets = (sepKind.data?.items ?? [])
    .filter((i) => i.inAccounts && !i.looksLikeSet && !i.isSplit)
    .map((i) => i.itemCode);
  const sepUnknown = (sepKind.data?.items ?? [])
    .filter((i) => !i.inAccounts)
    .map((i) => i.itemCode);

  const rowCount = parseRows(paste, kind).length;
  /** Rows built for one format, about to be read as the other. */
  const formatMismatch = pasteSource !== null && pasteSource !== kind;
  /** Rejections this screen can resolve — a child Smartlink has never heard of. */
  const creatable = (result?.rejected ?? []).filter((r) => MISSING_IN_SMARTLINK.test(r.reason));

  /**
   * Preview the products as soon as a check finds any missing, rather than waiting for a click.
   * The names are the thing worth seeing, and a button that has to be pressed before another
   * button works reads as broken (it did).
   */
  useEffect(() => {
    if (result?.dryRun && creatable.length > 0 && !created && !createProducts.isPending) {
      createProducts.mutate(false);
    }
    // Intentionally keyed on the check result only: re-running on every render of `created` or
    // the mutation would fire the preview in a loop.
  }, [result, creatable.length]);

  const lines = (t: string) => t.split('\n').map((l) => l.trim());
  const genParentList = lines(genParents).filter(Boolean);
  const genCodeList = lines(genCodes).filter(Boolean);
  const genNameList = lines(genNames);
  const genColourList = genCodeList.map((code, i) => ({ code, name: genNameList[i] ?? code }));
  /**
   * Colours or set pieces? Decided by whether the item is ALREADY SPLIT — never by its code.
   *
   * An item that was never separated into two boxes takes ONE code per colour, ending `AB`. Only a
   * split item needs a code per box. The suffix cannot tell them apart: of 1,467 accounting items
   * ending in `AB` only 924 are split, and `DRMAB4K0092AB` ends in `AB` incidentally.
   */
  const itemKind = useQuery({
    queryKey: ['skumap', 'item-kind', genParentList.join(',')],
    enabled: genParentList.length > 0,
    queryFn: () =>
      api.post<{
        items: Array<{
          itemCode: string;
          isSplit: boolean;
          pieces: number;
          /** Which pieces it is split into — UPF/LPF/SD as well as A/B. */
          pieceNames?: string[];
          colours: number;
          inAccounts: boolean;
          /** The description says it ships in two boxes — see looksLikeSet(). */
          looksLikeSet: boolean;
          description: string | null;
        }>;
        dropped?: number;
        // POST, not GET. These codes are a whole product line — 480 of them made a ~8 KB URL, and
        // nginx answered 414 Request-URI Too Large before the request ever reached the app. The
        // screen then said only "could not check these item codes". A body has no such limit.
      }>('/api/v1/skumap/item-kind', { codes: genParentList }),
  });
  const family = decideFamilyKind(
    (itemKind.data?.items ?? []).map((i) => ({
      itemCode: i.itemCode,
      isSplit: i.isSplit,
      looksLikeSet: i.looksLikeSet,
    })),
  );
  const setMode = family.kind === 'set';
  const generated =
    genColourList.length > 0 && (family.kind === 'set' || family.kind === 'colour')
      ? setMode
        ? // `family.split` is the same fact that drew the "set item" badge: which of these codes
          // are actually split into pieces. A set code ending in AB announces itself; one like
          // BFJOY152D124DW10FB1 does not, and is indistinguishable from a plain colour parent by
          // shape alone — so the generator is told rather than left to guess.
          // And WHICH pieces: a bed split into UPF/LPF/SD is coloured in those, never A/B.
          generateSetFamily(genParentList, genColourList, {
            knownSets: family.split,
            piecesByParent: Object.fromEntries(
              (itemKind.data?.items ?? [])
                .filter((i) => (i.pieceNames?.length ?? 0) > 0)
                .map((i) => [i.itemCode, i.pieceNames!]),
            ),
          })
        : generateColourFamily(genParentList, genColourList)
      : null;
  /** Only a set row has a piece; the emitted format follows from that. */
  const genRows = (generated?.rows ?? []) as Array<Record<string, string>>;

  return (
    <div className="space-y-6">
      <Card
        title="Find a mapping"
        subtitle="Enter a parent item code to see its family, or a tagged code to see what it rolls up to."
      >
        <form
          className="flex flex-wrap items-center gap-3"
          onSubmit={(e) => {
            e.preventDefault();
            setSubmitted(code.trim());
          }}
        >
          <input
            value={code}
            onChange={(e) => setCode(e.target.value)}
            placeholder="e.g. HBTF182FB1 or HBTF182CTE002"
            aria-label="Item code"
            className="w-80 rounded border border-slate-300 px-3 py-2 font-mono text-sm text-slate-900 placeholder:font-sans placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
          />
          <button
            type="submit"
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700"
          >
            Search
          </button>
          <label className="flex items-center gap-2 text-xs text-slate-600">
            <input
              type="checkbox"
              checked={includeInactive}
              onChange={(e) => setIncludeInactive(e.target.checked)}
            />
            include deactivated
          </label>
        </form>

        {search.isLoading && <Loading />}
        {search.data && (
          <>
            {/* How big is each family? The count is a real total, not the number of rows shown —
                the list is capped at 500 and a product line runs to ~81 colours. Searching one
                colour code still reports how many siblings it has. */}
            {(search.data.families ?? []).length > 0 && (
              <div className="mt-4 flex flex-wrap gap-2">
                {(search.data.families ?? []).map((f) => {
                  const deactivated =
                    f.colour.total - f.colour.active + (f.split.total - f.split.active);
                  return (
                    <div
                      key={f.parentSku}
                      className="rounded border border-slate-200 bg-brand-50 px-3 py-2 text-sm text-slate-700"
                    >
                      <span className="font-mono font-semibold text-slate-900">{f.parentSku}</span>{' '}
                      <span className="text-slate-500">has</span>{' '}
                      {f.colour.total > 0 && (
                        <strong>
                          {f.colour.total} colour{f.colour.total === 1 ? '' : 's'}
                        </strong>
                      )}
                      {f.colour.total > 0 && f.split.total > 0 && ' and '}
                      {f.split.total > 0 && (
                        <>
                          <strong>
                            {f.split.total} set piece{f.split.total === 1 ? '' : 's'}
                          </strong>
                          {/* The two are different things: the coloured pieces are what tags carry,
                              the plain pair is the fallback used when a document names no colour.
                              A single total hides which of them is actually set up. */}
                          {f.split.coloured > 0 && f.split.base > 0 && (
                            <span className="text-slate-500">
                              {' '}
                              — {f.split.coloured} coloured + {f.split.base} plain
                            </span>
                          )}
                          {f.split.coloured > 0 && f.split.base === 0 && (
                            <span className="text-slate-500"> — all coloured, no plain pair</span>
                          )}
                          {f.split.coloured === 0 && (
                            <span className="text-slate-500"> — plain only, no colours yet</span>
                          )}
                        </>
                      )}
                      {f.colour.total === 0 && f.split.total === 0 && <strong>no children</strong>}
                      {deactivated > 0 && (
                        <span className="text-slate-500"> · {deactivated} deactivated</span>
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            <h3 className="mb-2 mt-6 text-sm font-semibold text-slate-700">
              Colours{' '}
              <span className="font-normal text-slate-500">— quantities ADD to the parent</span>
            </h3>
            {search.data.colour.length === 0 ? (
              <Empty>No colour mappings for {search.data.code}.</Empty>
            ) : (
              <Table
                head={[
                  'Tagged code',
                  'Rolls up to',
                  'Colour',
                  'Source',
                  'Changed by',
                  'Status',
                  '',
                ]}
                rows={search.data.colour.map((r) => [
                  r.child_sku,
                  r.parent_sku,
                  r.colour_name ?? r.colour_code ?? '—',
                  r.source ?? '—',
                  r.updated_by ?? '—',
                  cell(
                    r.is_active ? 'active' : 'deactivated',
                    r.is_active ? (
                      <span className="text-emerald-700">active</span>
                    ) : (
                      <span className="text-slate-400">deactivated</span>
                    ),
                  ),
                  cell(
                    '',
                    <button
                      onClick={() =>
                        setActive.mutate({
                          childSku: r.child_sku,
                          active: !r.is_active,
                          kind: 'colour',
                        })
                      }
                      className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                    >
                      {r.is_active ? 'Deactivate' : 'Restore'}
                    </button>,
                  ),
                ])}
              />
            )}

            <h3 className="mb-2 mt-8 text-sm font-semibold text-slate-700">
              Sets{' '}
              <span className="font-normal text-slate-500">— quantities PAIR into one parent</span>
            </h3>
            {search.data.split.length === 0 ? (
              <Empty>No set mappings for {search.data.code}.</Empty>
            ) : (
              <Table
                head={[
                  'Tagged code',
                  'Rolls up to',
                  'Colour',
                  'Piece',
                  'Per set',
                  'Changed by',
                  'Status',
                  '',
                ]}
                rows={search.data.split.map((r) => [
                  r.child_sku,
                  r.parent_sku,
                  // A piece with no colour is the plain fabric pair — the fallback used when a
                  // document names no colour. Worth naming rather than showing as a blank.
                  cell(
                    r.colour_name ?? r.colour_code ?? '',
                    r.colour_name ?? r.colour_code ?? (
                      <span className="text-slate-400">plain (no colour)</span>
                    ),
                  ),
                  r.piece,
                  r.qty_per_set,
                  r.updated_by ?? '—',
                  cell(
                    r.is_active ? 'active' : 'deactivated',
                    r.is_active ? (
                      <span className="text-emerald-700">active</span>
                    ) : (
                      <span className="text-slate-400">deactivated</span>
                    ),
                  ),
                  // Set pieces were the only mappings with no way to switch them off from here.
                  // An item wrongly marked as a set — `(A+B)` in a description that does not mean
                  // two boxes — could not be corrected on this screen at all.
                  cell(
                    '',
                    <button
                      onClick={() =>
                        setActive.mutate({
                          childSku: r.child_sku,
                          active: !r.is_active,
                          kind: 'split',
                        })
                      }
                      className="rounded border border-slate-300 px-2 py-1 text-xs hover:bg-brand-50"
                    >
                      {r.is_active ? 'Deactivate' : 'Restore'}
                    </button>,
                  ),
                ])}
              />
            )}
          </>
        )}
      </Card>

      <Card
        title="Check many at once"
        subtitle="Paste a whole product line — one item code per line — to see what each one has and what it still needs."
      >
        <textarea
          rows={4}
          value={bulkCodes}
          onChange={(e) => setBulkCodes(e.target.value)}
          placeholder={'BFJOY152(12)FB1AB\nBFGROVE180FB1AB\nHBTF182FB1'}
          aria-label="Item codes to check"
          className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs text-slate-900 placeholder:font-sans placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
        />
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-500">
            {bulkList.length.toLocaleString()} item(s)
            {bulkList.length > CHECK_BATCH && (
              <span className="text-slate-400">
                {' '}
                — checked in {Math.ceil(bulkList.length / CHECK_BATCH)} batches
              </span>
            )}
          </span>
          {progress && bulkCheck.isPending && (
            <span className="text-xs font-medium text-brand-700">
              {progress.done.toLocaleString()} of {progress.total.toLocaleString()}…
            </span>
          )}
          <button
            type="button"
            disabled={bulkList.length === 0 || bulkCheck.isPending}
            onClick={() => bulkCheck.mutate(bulkList)}
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {bulkCheck.isPending
              ? 'Checking…'
              : `Check ${bulkList.length.toLocaleString()} item(s)`}
          </button>
        </div>

        {bulkCheck.data && (
          <div className="mt-4 space-y-3">
            {bulkCheck.data.dropped > 0 && (
              /* Should never fire now that the check is batched, but a silent short answer is
                 exactly the failure this whole panel exists to avoid. */
              <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
                <strong>{bulkCheck.data.dropped}</strong> item(s) were NOT checked — the server
                capped the request. The results below are incomplete.
              </p>
            )}
            {/* The counts by verdict first: with a hundred rows, "what needs doing" is the
                question, and scanning a hundred lines to find out is not an answer. */}
            <div className="flex flex-wrap gap-2">
              {(Object.keys(STATUS_STYLE) as Array<FamilyLine['status']>).map((k) => {
                const n = (bulkCheck.data?.items ?? []).filter((i) => i.status === k).length;
                if (n === 0) return null;
                return (
                  <span
                    key={k}
                    className={`rounded px-2 py-1 text-xs font-medium ${STATUS_STYLE[k].cls}`}
                  >
                    {n} {STATUS_STYLE[k].label}
                  </span>
                );
              })}
            </div>
            <Table
              head={['Item code', 'Colours', 'Set pieces', 'Status', 'What it needs']}
              rows={(bulkCheck.data?.items ?? []).map((i) => [
                cell(i.itemCode, <span className="font-mono text-xs">{i.itemCode}</span>),
                i.colours,
                i.splitColoured + i.splitBase === 0
                  ? cell(0, <span className="text-slate-400">—</span>)
                  : cell(
                      i.splitColoured + i.splitBase,
                      <span>
                        {i.splitColoured + i.splitBase}
                        <span className="text-xs text-slate-500">
                          {' '}
                          — {i.splitColoured} coloured + {i.splitBase} plain
                        </span>
                      </span>,
                    ),
                cell(
                  i.status,
                  <span
                    className={`rounded px-2 py-0.5 text-xs font-medium ${STATUS_STYLE[i.status].cls}`}
                  >
                    {STATUS_STYLE[i.status].label}
                  </span>,
                ),
                cell(i.needs, <span className="text-xs text-slate-600">{i.needs || '—'}</span>),
              ])}
            />
          </div>
        )}
        {bulkCheck.isError && (
          <p className="mt-3 rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
            Check failed: {(bulkCheck.error as Error).message}
          </p>
        )}
      </Card>

      <Card
        title="Add a colour family"
        subtitle="A new line is a handful of items in many colours. Enter the items once and the colours once — every combination is worked out for you. A set item (ending AB) gets two pieces per colour."
      >
        <div className="grid gap-4 sm:grid-cols-3">
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Item codes <span className="font-normal text-slate-400">one per line</span>
            </label>
            <textarea
              rows={10}
              value={genParents}
              onChange={(e) => setGenParents(e.target.value)}
              placeholder={'HBTF182FB1\nHBMC152FB1'}
              className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Colour codes <span className="font-normal text-slate-400">one per line</span>
            </label>
            <textarea
              rows={10}
              value={genCodes}
              onChange={(e) => setGenCodes(e.target.value)}
              placeholder={'CTE001\nCTE002'}
              className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none"
            />
          </div>
          <div>
            <label className="mb-1 block text-xs font-medium text-slate-600">
              Colour names{' '}
              <span className="font-normal text-slate-400">same order as the codes</span>
            </label>
            <textarea
              rows={10}
              value={genNames}
              onChange={(e) => setGenNames(e.target.value)}
              placeholder={'CTE 001 OYSTER\nCTE 002 PEWTER'}
              className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs focus:border-brand-500 focus:outline-none"
            />
          </div>
        </div>
        {/* The decision is shown, never silent: it changes how many products get created and which
            table the rows land in, and it is read from the data rather than from the code. */}
        {genParentList.length > 0 && itemKind.isLoading && (
          <p className="mt-3 text-xs text-slate-500">Checking whether these items are split…</p>
        )}
        {/* "If not yet, need to separate set first" — LSK, 2026-09-04. A genuine two-box set that
            nobody has separated cannot be coloured yet: one code per colour would never match a
            tag on either box. Offered as an action, not just a complaint. */}
        {family.kind === 'needs-split' && (
          <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-4 py-3">
            <p className="text-sm text-amber-900">
              <strong>{family.needsSplit.length}</strong> of these are two-box sets that have not
              been separated into A and B yet. Separate them first — their colours need a code per
              box.
            </p>
            <div className="mt-2 max-h-32 overflow-y-auto text-xs text-amber-800">
              {family.needsSplit.map((c) => (
                <div key={c} className="font-mono">
                  {c}
                </div>
              ))}
            </div>
            {family.split.length > 0 && (
              <p className="mt-2 text-xs text-amber-800">
                {family.split.length} of them are already separated and can be coloured once these
                are done.
              </p>
            )}
            {/* SQL Accounting is the authority on packing, and the first time this escape hatch
                was used the description was RIGHT and the person was wrong (BFJOY152D124DW10FB1,
                2026-09-15 — LSK later confirmed it IS a set and were adding AB to the code).
                Kept, because a master can genuinely be wrong; worded so it is the last resort it
                should be rather than the quick way past a blocked screen. */}
            <p className="mt-3 border-t border-amber-200 pt-2 text-xs text-amber-800">
              <strong>Check SQL Accounting first</strong> — the item description there is the
              authority on whether something ships in two boxes. Only if it is genuinely wrong:
              {family.needsSplit.map((c) => (
                <button
                  key={c}
                  type="button"
                  disabled={markNotASet.isPending}
                  onClick={() =>
                    markNotASet.mutate({
                      itemCode: c,
                      notASet: true,
                      reason: 'marked from the SKU mapping screen',
                    })
                  }
                  className="ml-2 rounded border border-amber-400 bg-white px-2 py-1 font-mono text-xs hover:bg-amber-100 disabled:opacity-50"
                >
                  {c} is not a set
                </button>
              ))}
            </p>
            <button
              type="button"
              onClick={() => {
                // Fill the box below with the A/B rows for each unseparated set, so the same
                // Check → Create → Save path does the separating.
                setKind('split');
                setPasteSource('split');
                setPaste(
                  family.needsSplit
                    .flatMap((code) => baseSetPieces(code))
                    .map((r) => [r.childSku, r.parentSku, r.piece, r.qtyPerSet, '', ''].join('\t'))
                    .join('\n'),
                );
                setResult(null);
                setCreated(null);
                setShowAllRejected(false);
                setFileName('');
              }}
              className="mt-3 rounded bg-amber-600 px-4 py-2 text-sm font-medium text-white hover:bg-amber-700"
            >
              Separate {family.needsSplit.length} set(s) — {family.needsSplit.length * 2} pieces
            </button>
          </div>
        )}

        {family.kind === 'mixed' && (
          <div className="mt-3 rounded border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-900">
            <strong>These items need two separate batches.</strong> {family.reason}
            <div className="mt-2 text-xs">
              <div>
                Split into pieces — a code per box: <code>{family.split.join(', ')}</code>
              </div>
              <div className="mt-1">
                Not split — one code per colour: <code>{family.notSplit.join(', ')}</code>
              </div>
            </div>
          </div>
        )}
        {(itemKind.data?.dropped ?? 0) > 0 && (
          <p className="mt-3 text-xs text-amber-700">
            Only the first {genParentList.length - (itemKind.data?.dropped ?? 0)} item code(s) were
            checked — {itemKind.data?.dropped} more were not. Do this product line in smaller
            batches so none go unchecked.
          </p>
        )}
        {!generated && genParentList.length > 0 && family.kind !== 'needs-split' && (
          <p className="mt-3 text-xs text-slate-600">
            {itemKind.isFetching
              ? 'Checking these item codes…'
              : itemKind.isError
                ? 'Could not check these item codes just now, so nothing can be worked out. Try again.'
                : genColourList.length === 0
                  ? 'Enter the colour codes too — the combinations are worked out from both lists.'
                  : family.kind === 'mixed'
                    ? 'Split the list into two batches as described above, then the mappings can be worked out.'
                    : `None of these ${genParentList.length} item code(s) were found in SQL Accounting, ` +
                      'so their family is unknown. Check the codes are spelled as the accounts have them.'}
          </p>
        )}
        {generated && (
          <p className="mt-3 text-xs text-slate-600">
            {genParentList.length} item(s) × {genColourList.length} colour(s) ={' '}
            <strong>{generated.rows.length}</strong> mapping(s)
            <span
              className={
                setMode
                  ? 'ml-2 rounded bg-amber-100 px-2 py-0.5 text-amber-900'
                  : 'ml-2 rounded bg-brand-50 px-2 py-0.5 text-slate-700'
              }
            >
              {setMode
                ? 'set item — two pieces per colour, mapped so quantities PAIR'
                : 'one code per colour — this item is not split into pieces'}
            </span>
            {generated.undecodable.length > 0 && (
              <span className="ml-2 text-amber-700">
                — {generated.undecodable.join(', ')}{' '}
                {'has no recognisable material code in it (like '}
                <span className="font-mono">FB1</span>
                {' or '}
                <span className="font-mono">PU1</span>
                {'), so no colour code can be worked out for it. Add those by paste or CSV.'}
              </span>
            )}
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={!generated || generated.rows.length === 0}
            onClick={() => {
              // Hand the result to the same box everything else goes through, so a generated row
              // is checked exactly like a typed one — the derivation is right 99.93% of the time,
              // not always.
              setKind(setMode ? 'split' : 'colour');
              setPasteSource(setMode ? 'split' : 'colour');
              setPaste(
                genRows
                  .map((r) =>
                    (setMode
                      ? // tagged code, parent, piece, qty per set, colour code, colour name.
                        // The colour is stored on the split row: the push builder uses it to choose
                        // WHICH colours to offer, or every order lists 592 phantom lines.
                        [
                          r['childSku'],
                          r['parentSku'],
                          r['piece'],
                          r['qtyPerSet'],
                          r['colourCode'],
                          r['colourName'],
                        ]
                      : [r['childSku'], r['parentSku'], r['colourCode'], r['colourName']]
                    ).join('\t'),
                  )
                  .join('\n'),
              );
              setResult(null);
              setCreated(null);
              setFileName('');
            }}
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            Work out {generated?.rows.length ?? 0} mapping(s)
          </button>
          <span className="text-xs text-slate-500">
            They appear below for checking before anything is saved.
          </span>
        </div>
      </Card>

      <Card
        title="Separate sets into pieces"
        subtitle="Enter the item codes. The piece codes are worked out for you. Most sets are two boxes (A and B); set the default pieces below, or paste two columns from Excel — item code and pieces (e.g. UPF/LPF/SD) — to choose per item."
      >
        <div className="mb-3 flex flex-wrap items-end gap-3">
          <label className="text-xs text-slate-600">
            <span className="mb-1 block">Pieces</span>
            <input
              value={sepPieces}
              onChange={(e) => setSepPieces(e.target.value)}
              placeholder="AB"
              aria-label="Default pieces"
              className="w-32 rounded border border-slate-300 px-3 py-2 font-mono text-sm uppercase text-slate-900 focus:border-brand-500 focus:outline-none"
            />
          </label>
          <span className="pb-2 text-xs text-slate-500">
            <code className="rounded bg-slate-100 px-1">AB</code> for two boxes, or any names
            separated by <code className="rounded bg-slate-100 px-1">/</code> — e.g.{' '}
            <code className="rounded bg-slate-100 px-1">UPF/LPF/SD</code>,{' '}
            <code className="rounded bg-slate-100 px-1">AA/BB/CC</code>. Letters and digits, up to 8
            each. Check the codes and names below before saving — a product cannot be deleted.
          </span>
        </div>
        <textarea
          rows={4}
          value={sepCodes}
          onChange={(e) => setSepCodes(e.target.value)}
          placeholder={'BFJOY152(12)FB1AB\nDVHDLPRS15219014FB12P\tUPF/LPF/SD\nSOMEBEDFRAME\tUPF/SD'}
          aria-label="Set item codes to separate"
          className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs text-slate-900 placeholder:font-sans placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
        />
        {sepRows.length > 0 && (
          <p className="mt-3 text-xs text-slate-600">
            {sepList.length - sepUndecodable.length} set(s) = <strong>{sepRows.length}</strong>{' '}
            piece(s)
            <span className="ml-2 text-slate-500">
              e.g. {sepRows[0]?.childSku} + {sepRows[1]?.childSku}
            </span>
          </p>
        )}
        {sepUndecodable.length > 0 && (
          <p className="mt-2 text-xs text-amber-700">
            {sepUndecodable.join('; ')}. A set needs at least two different pieces.
          </p>
        )}
        {sepUnknown.length > 0 && (
          <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <strong>{sepUnknown.join(', ')}</strong> —{' '}
            {sepUnknown.length === 1 ? 'this code is' : 'these codes are'} not in our copy of the
            item master, and a live check did not find {sepUnknown.length === 1 ? 'it' : 'them'}{' '}
            either. Usually a typo — but if the code was created in SQL Accounting in the last few
            minutes, give it a moment and search again.
          </p>
        )}
        {sepNotSets.length > 0 && (
          /* The derivation appends a letter to anything, so the description is what stops a
             headboard being split into two boxes that will never exist. */
          <p className="mt-2 rounded border border-amber-300 bg-amber-50 px-3 py-2 text-xs text-amber-900">
            <strong>{sepNotSets.join(', ')}</strong> — SQL Accounting does not describe{' '}
            {sepNotSets.length === 1 ? 'this' : 'these'} as a multi-piece item. Check before
            creating: splitting something that ships in one box makes codes no tag will carry.
          </p>
        )}
        <div className="mt-3 flex flex-wrap items-center gap-3">
          <button
            type="button"
            disabled={sepRows.length === 0}
            onClick={() => {
              // Straight into the same box everything else goes through, so a derived row is
              // checked exactly like a typed one before anything is written.
              setKind('split');
              setPasteSource('split');
              setPaste(
                sepRows
                  .map((r) => [r.childSku, r.parentSku, r.piece, r.qtyPerSet, '', ''].join('\t'))
                  .join('\n'),
              );
              setResult(null);
              setCreated(null);
              setShowAllRejected(false);
              setFileName('');
            }}
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            Work out {sepRows.length} piece(s)
          </button>
          <span className="text-xs text-slate-500">
            They appear below for checking before anything is saved.
          </span>
        </div>
      </Card>

      <Card
        title="Add or update in bulk"
        subtitle="Upload a CSV or paste from a spreadsheet. Always check before saving — a wrong mapping becomes a wrong item code on a goods received note."
      >
        <div className="mb-3 flex flex-wrap items-center gap-x-6 gap-y-2">
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              checked={kind === 'colour'}
              onChange={() => setKind('colour')}
              className="accent-brand-600"
            />
            <span>
              Colours —{' '}
              <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                tagged code, parent code, colour code, colour name
              </code>
            </span>
          </label>
          <label className="flex cursor-pointer items-center gap-2 text-sm text-slate-700">
            <input
              type="radio"
              checked={kind === 'split'}
              onChange={() => setKind('split')}
              className="accent-brand-600"
            />
            <span>
              Sets —{' '}
              <code className="rounded bg-slate-100 px-1.5 py-0.5 font-mono text-xs text-slate-600">
                tagged code, parent code, piece (A/B), qty per set, colour code, colour name
              </code>
            </span>
          </label>
        </div>

        <div className="mb-3 flex flex-wrap items-center gap-3">
          <label className="cursor-pointer rounded border border-slate-300 px-4 py-2 text-sm font-medium text-slate-700 hover:bg-brand-50">
            Choose CSV file…
            <input
              type="file"
              accept=".csv,.txt,text/csv"
              className="hidden"
              onChange={(e) => {
                const file = e.target.files?.[0];
                if (!file) return;
                setFileName(file.name);
                setResult(null);
                setCreated(null);
                setPasteSource(null);
                void file.text().then(setPaste);
                // Clear the picker so choosing the SAME file again still fires a change event.
                e.target.value = '';
              }}
            />
          </label>
          {fileName && <span className="text-xs text-slate-500">{fileName}</span>}
          <button
            type="button"
            onClick={() => downloadTemplate(kind)}
            className="text-xs text-brand-600 underline"
          >
            Download {kind === 'colour' ? 'colour' : 'set'} template
          </button>
        </div>

        <p className="mb-2 text-xs text-slate-500">
          Or paste rows straight from a spreadsheet. A header row is optional.
        </p>

        <textarea
          rows={8}
          value={paste}
          onChange={(e) => {
            setPaste(e.target.value);
            setResult(null);
            setCreated(null);
            setShowAllRejected(false);
            setPasteSource(null);
            setFileName('');
          }}
          placeholder={'HBTF182CTE002\tHBTF182FB1\tCTE002\tCTE 002 PEWTER'}
          aria-label="Rows to upload"
          className="w-full rounded border border-slate-300 px-3 py-2 font-mono text-xs text-slate-900 placeholder:text-slate-400 focus:border-brand-500 focus:outline-none"
        />

        {formatMismatch && (
          /* The two layouts share their first two columns, so this mismatch validates cleanly and
             writes pieces into the colour table. Blocked rather than warned about. */
          <div className="mt-3 rounded border border-alert/40 bg-alert/10 px-4 py-3 text-sm text-brand-900">
            <strong>
              These rows were worked out for {pasteSource === 'split' ? 'Sets' : 'Colours'}.
            </strong>{' '}
            Reading them as {kind === 'split' ? 'Sets' : 'Colours'} would put the wrong values in
            every column — a piece would be stored as a colour, and quantities would{' '}
            {kind === 'split' ? 'pair instead of adding' : 'add instead of pairing'}.
            <button
              type="button"
              onClick={() => setKind(pasteSource === 'split' ? 'split' : 'colour')}
              className="ml-2 rounded border border-alert/40 px-2 py-1 text-xs font-medium hover:bg-alert/10"
            >
              Switch back to {pasteSource === 'split' ? 'Sets' : 'Colours'}
            </button>
          </div>
        )}

        <div className="mt-3 flex flex-wrap items-center gap-3">
          <span className="text-xs text-slate-500">{rowCount} row(s) ready</span>
          {/* Eleven round trips for a full product line: silence for that long reads as a hang. */}
          {progress && (
            <span className="text-xs font-medium text-brand-700">
              {progress.done.toLocaleString()} of {progress.total.toLocaleString()}…
            </span>
          )}
          <button
            type="button"
            disabled={rowCount === 0 || upload.isPending || formatMismatch}
            onClick={() => upload.mutate(false)}
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {/* `variables` is the dryRun flag: false = the check, true = the save. */}
            {upload.isPending && upload.variables === false ? 'Checking…' : 'Check without saving'}
          </button>
          <button
            type="button"
            disabled={
              !result ||
              result.dryRun === false ||
              upload.isPending ||
              saveAll.isPending ||
              createProducts.isPending ||
              formatMismatch ||
              (creatable.length > 0 && !created)
            }
            onClick={() => saveAll.mutate()}
            className="rounded bg-brand-600 px-4 py-2 text-sm font-medium text-white hover:bg-brand-700 disabled:cursor-not-allowed disabled:bg-slate-300"
          >
            {/* Names BOTH actions. Creating a product cannot be undone, so the button that does it
                has to say so — this is the only confirmation there is. */}
            {saveAll.isPending
              ? 'Working…'
              : !result?.dryRun
                ? 'Save'
                : creatable.length > 0
                  ? // wouldWrite is 0 right now BECAUSE the products do not exist yet; once they
                    // do, those rows pass. Promising "save 0" for work that will save 19,680 reads
                    // as a broken button.
                    `Create ${creatable.length.toLocaleString()} product(s) & save ${(
                      (result.wouldWrite ?? 0) + creatable.length
                    ).toLocaleString()} row(s)`
                  : `Save ${(result.wouldWrite ?? 0).toLocaleString()} row(s)`}
          </button>
        </div>

        {/* The check must be run before saving: a wrong mapping here is a wrong item code on a
            goods received note, and that is only discovered once goods are on the floor. */}
        {result && (
          <div className="mt-4 space-y-3">
            {result.dryRun ? (
              <p className="rounded border border-slate-200 bg-brand-50 px-4 py-2 text-sm text-slate-700">
                <strong>{result.wouldWrite}</strong> row(s) would be saved,{' '}
                <strong>{result.rejected.length}</strong> rejected.{' '}
                <span className="text-slate-500">Nothing has been written yet.</span>
              </p>
            ) : null}
            {result.dryRun && (result.retire?.length ?? 0) > 0 && (
              /* A set completes only when EVERY mapped piece is scanned, so the old split cannot
                 stay live beside the new one. Shown before saving because it changes what scans
                 of these items resolve to. Retired rows are deactivated, never deleted. */
              <div className="rounded border border-amber-300 bg-amber-50 px-4 py-2 text-sm text-amber-900">
                <p>
                  Saving will <strong>retire {result.retire!.length}</strong> existing piece
                  mapping(s) — these items were split into different pieces before:
                </p>
                <ul className="mt-1 max-h-40 overflow-auto font-mono text-xs">
                  {result.retire!.map((r) => (
                    <li key={r.childSku}>
                      {r.childSku} → {r.parentSku} (piece {r.piece})
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {!result.dryRun && (
              <p className="rounded border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-900">
                Saved <strong>{result.written}</strong> row(s).
                {(result.retired ?? 0) > 0 && (
                  <>
                    {' '}
                    Retired <strong>{result.retired}</strong> old piece mapping(s).
                  </>
                )}
              </p>
            )}
            {/* The one rejection this screen can fix: a colour Smartlink has never heard of.
                Creating it here is what makes a new colour self-service — it used to mean a
                round-trip through the integration team. */}
            {result.dryRun && creatable.length > 0 && (
              <div className="rounded border border-amber-300 bg-amber-50 px-4 py-3">
                <p className="text-sm text-amber-900">
                  <strong>{creatable.length}</strong> of these are new colours Smartlink does not
                  have yet. They can be created here — a tag can only carry a code Smartlink knows.
                </p>
                <p className="mt-1 text-xs text-amber-800">
                  Style, size and stock group are copied from the parent item. There is no way to
                  delete a product afterwards, so read the names below before saving — Save creates
                  them and writes the mappings in one go.
                </p>
                <div className="mt-3 flex flex-wrap items-center gap-3">
                  <button
                    type="button"
                    disabled={createProducts.isPending}
                    onClick={() => createProducts.mutate(false)}
                    className="rounded border border-amber-400 bg-white px-4 py-2 text-sm font-medium text-amber-900 hover:bg-amber-100 disabled:cursor-not-allowed disabled:text-slate-400"
                  >
                    {createProducts.isPending
                      ? 'Reading…'
                      : `Refresh preview (${creatable.length})`}
                  </button>
                </div>
              </div>
            )}

            {created && (
              <div className="space-y-2">
                {created.error && (
                  <p className="rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
                    Smartlink refused: {created.error}
                  </p>
                )}
                {created.dryRun && !created.error && (
                  <p className="rounded border border-slate-200 bg-brand-50 px-4 py-2 text-sm text-slate-700">
                    <strong>{creatable.length.toLocaleString()}</strong> product(s) would be created
                    {created.simulated ? ' (DRY_RUN — nothing was sent)' : ''}.{' '}
                    {creatable.length > (created.wouldCreate ?? 0) && (
                      <span className="text-slate-500">
                        Names below are the first {created.wouldCreate} as a sample.{' '}
                      </span>
                    )}
                    <span className="text-slate-500">Nothing has been created yet.</span>
                  </p>
                )}
                {!created.dryRun && !created.error && (
                  <p className="rounded border border-emerald-300 bg-emerald-50 px-4 py-2 text-sm text-emerald-900">
                    Created <strong>{created.created}</strong> product(s) in Smartlink. Re-checked —
                    they can be saved as mappings now.
                  </p>
                )}
                {created.dryRun && (created.create?.length ?? 0) > 0 && (
                  /* Both sides of the pairing. There is no way to delete a product once created,
                     so this table is the last chance to catch a wrong parent. */
                  <Table
                    head={[
                      'New code',
                      'New product name',
                      'Size',
                      'Parent code',
                      'Parent product name',
                    ]}
                    rows={(created.create ?? []).map((c) => [
                      c.sku,
                      c.styleName ?? '—',
                      c.sizeName ?? '—',
                      c.parentSku,
                      c.parentName ?? '—',
                    ])}
                  />
                )}
                {created.refused.length > 0 && (
                  <Table
                    head={['Code', 'Cannot be created because']}
                    rows={created.refused.map((r) => [r.childSku || '—', r.reason])}
                  />
                )}
              </div>
            )}

            {result.rejected.length > 0 && (
              /* Truncated on purpose. A whole product line is ~81 colours and a bad item code
                 rejects every one of them, so the full list is a wall that buries the action
                 above it rather than explaining anything the first few rows do not. */
              <div>
                <Table
                  head={['Row', 'Tagged code', 'Parent', 'Why it was rejected']}
                  rows={(showAllRejected ? result.rejected : result.rejected.slice(0, 20)).map(
                    (r) => [r.row, r.childSku, r.parentSku, r.reason],
                  )}
                />
                {result.rejected.length > 20 && (
                  <button
                    type="button"
                    onClick={() => setShowAllRejected((v) => !v)}
                    className="mt-2 text-xs text-brand-600 underline"
                  >
                    {showAllRejected
                      ? 'Show fewer'
                      : `Show all ${result.rejected.length} rejected rows`}
                  </button>
                )}
              </div>
            )}
          </div>
        )}
        {(createProducts.isError || saveAll.isError) && (
          /* Without this a failed preview just left the Save button greyed out forever, with
             nothing on screen saying why. */
          <p className="mt-3 rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
            {((createProducts.error ?? saveAll.error) as Error).message}
          </p>
        )}
        {upload.isError && (
          <p className="mt-3 rounded border border-alert/40 bg-alert/10 px-4 py-2 text-sm text-brand-900">
            Upload failed: {(upload.error as Error).message}
          </p>
        )}
      </Card>
    </div>
  );
}
